import { createContext, useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeResponse, SignInRequest } from '@apis/contracts';
import { useApi } from './api';
import { qk } from './query-keys';

/** Current principal from `/v1/me`. A 401 surfaces as an ApiError with status 401. */
export function useMe() {
  const api = useApi();
  return useQuery({ queryKey: qk.me, queryFn: () => api.me(), staleTime: 60_000 });
}

export function useSignIn() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SignInRequest) => api.signIn(body),
    onSuccess: (me) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== qk.me[0] });
      qc.setQueryData<MeResponse>(qk.me, me);
    },
  });
}

export function useSignOut(onSignedOut: () => void) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.signOut(),
    onSettled: () => {
      // Drop every cached response that belonged to the old session, success or not.
      qc.clear();
      onSignedOut();
    },
  });
}

export function useSwitchTenant() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => api.switchTenant(tenantId),
    onSuccess: (me) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== qk.me[0] });
      qc.setQueryData<MeResponse>(qk.me, me);
    },
  });
}

export const SessionContext = createContext<MeResponse | null>(null);

/** The signed-in principal (inside the session gate). */
export function useSession(): MeResponse {
  const me = useContext(SessionContext);
  if (!me) throw new Error('useSession must be used inside <SessionGate>');
  return me;
}
